import { useApolloClient } from '@apollo/client';
import { useDisclosure, usePrevious, useToast } from '@chakra-ui/react';

import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { isEqual } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLogOperationCall } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import {
  GET_EDR_CONFIGURATION,
  IVerifyEDRConfiguration,
  useDeleteEDRConfiguration,
  useGetEDRConfiguration,
  useGetEDRConfigurationPublic,
  useStoreEDRConfiguration,
  useVerifyEDRConfiguration,
} from '@investigator/features/entities/queries';
import { IntegrationsModalProps } from '../../pages/IntegrationsConstants';
import { EntraIDSettingsForm } from './EntraIDSettingsForm';
import { EntraIdSettingsFooter } from './EntraIdSettingsFooter';
import { EntraIdSettingsDeleteModal } from './EntraIdSettingsDeleteModal';
import { EntraIDFormData } from '../../queries';

const { integrations } = i18n.pages;
const {
  save,
  cancel,
  error: errorText,
  success,
  warning,
  paloAlto: { verifyError },
} = integrations;

const {
  noGoBack,
  yesDelete,
  enabled: enabledLabel,
  disabled: disabledLabel,
  connectionNotVerifiedLabel,
} = integrations;

const {
  saveSuccess,
  saveError,
  deleteError,
  deleteSuccess,
  verifyConnection,
  deleteResult,
  deleteWarning,
  MicrosoftEntraIDIntegration,
  entraFormFields,
  modalUI,
  receiveEntityEnrichmentEntra,
  connectionVerified,
  detectionsCanNotBeEscalatedToEntra,
} = integrations.entra;

// Subset of IntegrationsModalProps used by this modal
type EntraIDSettingsCardModalProps = Pick<
  IntegrationsModalProps,
  'isOpen' | 'onClose'
>;
export const DEFAULT_POLLING_SECONDS = 30;

const defaultFormState: EntraIDFormData = {
  enabled: false,
  edr_action_enabled: false,
  entra_client_id: '',
  entra_client_secret: '',
  entra_tenant_id: '',
  polling_seconds: DEFAULT_POLLING_SECONDS,
  identity_enrichment_enabled: false,
};

export const EntraIDSettingsCardModal = ({
  isOpen,
  onClose,
}: EntraIDSettingsCardModalProps) => {
  const client = useApolloClient();
  const showToast = useToast();
  const [formIsReady, setFormIsReady] = useState<boolean>(false);
  const {
    isOpen: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose,
  } = useDisclosure();

  const { userRole, setUserPrivileges, userPrivileges, userLicense } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const getEDRConfigQuery =
    userRole === 'admin'
      ? useGetEDRConfiguration
      : useGetEDRConfigurationPublic;

  const logOperationCall = useLogOperationCall();

  const {
    watch,
    control,
    register,
    reset,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<EntraIDFormData>({
    mode: 'onChange',
    defaultValues: defaultFormState,
  });

  const formData = watch();
  const previousFormData = usePrevious(formData);

  const [appSecretInputPwType, setAppSecretInputPwType] =
    useState<boolean>(true);

  const [configurationVerified, setConfigurationVerified] = useState<
    boolean | undefined
  >(undefined);

  const {
    loading: EDRConfigLoading,
    error: EDRConfigError,
    data: EDRData,
  } = getEDRConfigQuery({
    variables: {
      provider_name: 'microsoft_entra_id',
    },
    fetchPolicy: 'cache-first',
    skip: !isOpen,
    onCompleted: (data) => {
      const config = data.getEDRConfiguration;
      if (!config) {
        reset(defaultFormState);
        setFormIsReady(true);
        return;
      }
      const { provider_name: _, polling_seconds, ...rest } = config;
      reset({
        ...rest,
        polling_seconds: polling_seconds
          ? polling_seconds / 60
          : DEFAULT_POLLING_SECONDS,
      });
      setFormIsReady(true);
    },
  });

  const handleClose = useCallback(() => {
    if (EDRData?.getEDRConfiguration === null) {
      reset(defaultFormState);
    } else if (EDRData?.getEDRConfiguration) {
      const { provider_name: _, ...rest } = EDRData.getEDRConfiguration;
      reset({ ...rest });
    }
    onClose();
  }, [EDRData, reset, onClose]);

  // Memoized onCompleted callback
  const onVerifyCompleted = useCallback(
    (data: IVerifyEDRConfiguration) => {
      const verified = data?.verifyEDRConfiguration?.verified;

      setConfigurationVerified(verified);

      if (verified === false) {
        showToast({
          title: errorText,
          description: detectionsCanNotBeEscalatedToEntra,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      }
    },
    [showToast],
  );

  // Memoized onError callback
  const onVerifyError = useCallback(() => {
    showToast({
      title: errorText,
      description: verifyError,
      status: 'error',
      isClosable: true,
      position: 'bottom-right',
    });
  }, [showToast]);

  const [verifyEDRConfiguration, { loading: verifyEDRConfigurationLoading }] =
    useVerifyEDRConfiguration({
      fetchPolicy: 'cache-first',
      onCompleted: onVerifyCompleted,
      onError: onVerifyError,
    });

  const handleVerify = useCallback(() => {
    verifyEDRConfiguration({
      variables: {
        provider_name: 'microsoft_entra_id',
        config: {
          microsoft_entra_id: {
            ...formData,
            polling_seconds: formData.polling_seconds * 60,
          },
        },
      },
    });
  }, [formData, verifyEDRConfiguration]);

  const [storeEDRConfiguration, { loading: storeEDRConfigurationLoading }] =
    useStoreEDRConfiguration({
      onCompleted: async () => {
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'getEDRConfiguration',
        });
        client.cache.gc();
        showToast({
          title: success,
          description: saveSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const updatedInput = {
          status: formData.enabled ? modalUI.enabled : modalUI.disabled,
          service_name: modalUI.microsoftEntraID,
        };

        logOperationCall(
          'ENABLE_DISABLE_CONFIGURATION',
          'INTEGRATION',
          JSON.stringify(updatedInput),
        );
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].microsoft_entra_id = formData.enabled;
        newUserPrivileges[tenant ?? ''].microsoft_entra_id_action_enabled =
          formData.edr_action_enabled;
        setUserPrivileges(newUserPrivileges);
        handleClose();
      },
      onError: async () => {
        showToast({
          title: errorText,
          description: saveError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [deleteEDRConfiguration, { loading: deleteEDRConfigurationLoading }] =
    useDeleteEDRConfiguration({
      variables: {
        provider_name: 'microsoft_entra_id',
      },
      refetchQueries: [
        {
          query: GET_EDR_CONFIGURATION,
          variables: {
            provider_name: 'microsoft_entra_id',
          },
        },
      ],
      onCompleted: async () => {
        showToast({
          title: success,
          description: deleteSuccess,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
        const newUserPrivileges = { ...userPrivileges };
        newUserPrivileges[tenant ?? ''].microsoft_entra_id = false;
        newUserPrivileges[tenant ?? ''].microsoft_entra_id_action_enabled =
          false;
        setUserPrivileges(newUserPrivileges);
        reset(defaultFormState);
        setConfigurationVerified(undefined);
        onClose();
      },
      onError: async () => {
        showToast({
          title: errorText,
          description: deleteError,
          status: 'error',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  useEffect(() => {
    if (
      previousFormData !== undefined &&
      !isEqual(previousFormData, formData)
    ) {
      setConfigurationVerified(undefined);
    }
  }, [formData, previousFormData]);

  return EDRConfigError || EDRConfigLoading || !formIsReady ? (
    <GraphQLReqStatus
      error={EDRConfigError}
      loading={EDRConfigLoading || !formIsReady}
      isBackground={true}
    ></GraphQLReqStatus>
  ) : (
    <>
      <Modal
        size="lg"
        isOpen={isOpen}
        onClose={handleClose}
        isCentered={true}
        title={MicrosoftEntraIDIntegration}
        closeOnEsc={!verifyEDRConfigurationLoading}
        isCloseDisabled={verifyEDRConfigurationLoading}
        closeOnOverlayClick={!verifyEDRConfigurationLoading}
        body={
          <EntraIDSettingsForm
            control={control}
            register={register}
            formData={formData}
            isValid={isValid}
            appSecretInputPwType={appSecretInputPwType}
            setAppSecretInputPwType={setAppSecretInputPwType}
            configurationVerified={configurationVerified}
            verifyLoading={verifyEDRConfigurationLoading}
            onVerify={handleVerify}
            onDeleteConnection={deleteOnOpen}
            setValue={setValue}
            labels={{
              receiveEntityEnrichmentEntra,
              enabledLabel,
              disabledLabel,
              connectionsSection: modalUI.connectionsSection,
              connectionVerified,
              connectionNotVerifiedLabel,
              removeConnection: modalUI.removeConnection,
              verifyConnection,
              settingsSection: modalUI.settingsSection,
              identityEnrichment: modalUI.identityEnrichment,
              identityEnrichmentDescription:
                modalUI.identityEnrichmentDescription,
              responseActions: modalUI.responseActions,
              responseActionsDescription: modalUI.responseActionsDescription,
              entraFormFields,
            }}
          />
        }
        footer={
          <EntraIdSettingsFooter
            isLoading={storeEDRConfigurationLoading}
            isValid={isValid}
            isDirty={isDirty}
            configurationVerified={configurationVerified}
            formEnabled={formData.enabled}
            cancelLabel={cancel}
            saveLabel={save}
            onCancel={handleClose}
            onSave={() =>
              storeEDRConfiguration({
                variables: {
                  provider_name: 'microsoft_entra_id',
                  config: {
                    microsoft_entra_id: {
                      ...formData,
                      polling_seconds: formData.polling_seconds * 60,
                    },
                  },
                },
              })
            }
          />
        }
      />
      <EntraIdSettingsDeleteModal
        isOpen={deleteIsOpen}
        isLoading={deleteEDRConfigurationLoading}
        onClose={deleteOnClose}
        onConfirm={() => {
          deleteEDRConfiguration();
          deleteOnClose();
        }}
        labels={{
          title: modalUI.deleteTitle,
          warningTitle: warning,
          warningDescription: deleteWarning,
          warningResult: deleteResult,
          cancel: noGoBack,
          confirm: yesDelete,
        }}
      />
    </>
  );
};
